/* eslint-disable no-useless-escape */
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import React, { FC, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { TFunction } from 'i18next';
import { BaseButton } from 'components/BaseButton';
import useAlert from 'utils/alert';

interface RawMessageModalProps {
  open: boolean;
  handleClose: () => void;
  rawMessage: string;
  title: string;
  t: TFunction;
}

const useStyles = makeStyles((theme: any) =>
  createStyles({
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexShrink: 0,
      padding: theme.spacing(0, 1, 0, 0),
      [theme.breakpoints.up('sm')]: { padding: theme.spacing(1) },
    },
    content: {
      paddingTop: theme.spacing(1),
    },
    rawBlock: {
      margin: 0,
      padding: theme.spacing(2),
      boxSizing: 'border-box',
      fontFamily: 'inherit',
      fontSize: '0.875rem',
      lineHeight: 1.5,
      color: theme.palette.primary.dark,
      whiteSpace: 'pre-wrap',
      overflowWrap: 'anywhere',
      background: theme.palette.background.default,
      border: `1px solid ${theme.palette.border.line}`,
      borderRadius: 4,
    },
    actions: {
      justifyContent: 'flex-end',
      gap: theme.spacing(1),
      padding: theme.spacing(1, 2, 2),
    },
  })
);

const RawMessageModal: FC<RawMessageModalProps> = ({ open, handleClose, rawMessage, title, t }) => {
  const classes = useStyles();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const showAlert = useAlert();

  const copyRawMessage = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(rawMessage);
      showAlert(t('common:copy-text'), 'success');
    } catch (err: any) {
      showAlert(err.message, 'error');
    }
  }, [rawMessage, showAlert, t]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen={fullScreen}
      maxWidth="md"
    >
      <Box className={classes.header}>
        <DialogTitle>
          <Typography color="primary" variant="h5" component="h2">{title}</Typography>
        </DialogTitle>
        <IconButton aria-label={t('common:close')} onClick={handleClose} size="large">
          <FontAwesomeIcon icon="times" />
        </IconButton>
      </Box>
      <DialogContent className={classes.content}>
        <Box component="pre" className={classes.rawBlock} tabIndex={0}>{rawMessage}</Box>
      </DialogContent>
      <DialogActions className={classes.actions}>
        <BaseButton
          buttonVariant="outlined"
          buttonColor="inherit"
          icon="times"
          text={t('common:close')}
          onClick={handleClose}
        />
        <BaseButton
          buttonVariant="contained"
          buttonColor="secondary"
          icon="copy"
          text={t('common:copy-text')}
          onClick={copyRawMessage}
        />
      </DialogActions>
    </Dialog>
  );
};

export default RawMessageModal;
